import { useMutation, useQuery, useQueryClient, UseQueryResult } from "@tanstack/react-query";
import { startRegistration } from "@simplewebauthn/browser";
import { apiFetch, ApiError } from "../../api/client";

export const TOTP_STATUS_KEY = ["totp-status"] as const;
export const PASSKEYS_KEY = ["passkeys"] as const;

export interface TotpStatus {
  readonly enabled: boolean;
  readonly recoveryCodesRemaining: number;
}

export interface Passkey {
  readonly id: string;
  readonly credentialId: string;
  readonly deviceName: string | null;
  readonly createdAt: string;
  readonly lastUsedAt: string | null;
}

export function useTotpStatus(): UseQueryResult<TotpStatus, ApiError> {
  return useQuery<TotpStatus, ApiError>({
    queryKey: TOTP_STATUS_KEY,
    queryFn: ({ signal }) => apiFetch<TotpStatus>("/auth/totp/status", { signal }),
  });
}

export interface TotpEnrollment {
  readonly otpauthUri: string;
  readonly secret: string;
}

export function useTotpEnroll() {
  return useMutation<TotpEnrollment, ApiError, void>({
    mutationFn: () => apiFetch<TotpEnrollment>("/auth/totp/enroll", { method: "POST", body: {} }),
  });
}

export function useTotpConfirm() {
  const qc = useQueryClient();
  return useMutation<{ recoveryCodes: string[] }, ApiError, { code: string }>({
    mutationFn: (input) =>
      apiFetch<{ recoveryCodes: string[] }>("/auth/totp/confirm", { method: "POST", body: input }),
    // `void`, NOT returned. react-query awaits a returned onSuccess before it
    // dispatches success, and that dispatch is what runs the per-call onSuccess
    // handing the recovery codes to the UI. Returning the invalidation would
    // gate the ONLY display of those codes on a second network round trip — one
    // that can hang, permanently losing the user's account recovery even though
    // confirm already returned 200 and burned the previous codes.
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: TOTP_STATUS_KEY });
    },
  });
}

/** Retire the current recovery codes and issue a fresh set (re-verifying a
 *  live TOTP code). Same `void`-not-return rule as confirm: the new codes are
 *  shown once and must not wait on a refetch. */
export function useTotpRegenerateCodes() {
  const qc = useQueryClient();
  return useMutation<{ recoveryCodes: string[] }, ApiError, { code: string }>({
    mutationFn: (input) =>
      apiFetch<{ recoveryCodes: string[] }>("/auth/totp/recovery-codes", {
        method: "POST",
        body: input,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: TOTP_STATUS_KEY });
    },
  });
}

export function useTotpDisable() {
  const qc = useQueryClient();
  return useMutation<void, ApiError, { code: string }>({
    mutationFn: (input) => apiFetch<void>("/auth/totp/disable", { method: "POST", body: input }),
    onSuccess: () => qc.invalidateQueries({ queryKey: TOTP_STATUS_KEY }),
  });
}

export function usePasskeys(): UseQueryResult<Passkey[], ApiError> {
  return useQuery<Passkey[], ApiError>({
    queryKey: PASSKEYS_KEY,
    queryFn: async ({ signal }) => {
      const res = await apiFetch<{ passkeys: Passkey[] }>("/auth/passkeys", { signal });
      return res.passkeys;
    },
  });
}

export function useRegisterPasskey() {
  const qc = useQueryClient();
  return useMutation<void, Error, { deviceName?: string }>({
    mutationFn: async ({ deviceName }) => {
      const optionsJSON = await apiFetch<Parameters<typeof startRegistration>[0]["optionsJSON"]>(
        "/auth/passkey/register/options",
        { method: "POST", body: {} },
      );
      const response = await startRegistration({ optionsJSON });
      await apiFetch<{ ok: true }>("/auth/passkey/register/verify", {
        method: "POST",
        body: { response, deviceName },
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: PASSKEYS_KEY }),
  });
}

export function useDeletePasskey() {
  const qc = useQueryClient();
  return useMutation<void, ApiError, { id: string }>({
    mutationFn: ({ id }) => apiFetch<void>(`/auth/passkeys/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: PASSKEYS_KEY }),
  });
}
